/**
 * Normalises a string for searching:
 *  - lowercase
 *  - replace common separators (hyphens, underscores, slashes, etc.) with a space
 *  - collapse multiple spaces
 */
const normalize = (str) =>
  (str || '')
    .toLowerCase()
    .replace(/[-_/\\.,;:!?()&]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Collapsed form: separators AND spaces removed entirely.
 * "K-Pop" → "kpop", "Dark Chocolate" → "darkchocolate"
 * Allows "kpop" to match "K-Pop" and "darkchoc" to fuzzy-match.
 */
const normalizeCollapsed = (str) =>
  (str || '')
    .toLowerCase()
    .replace(/[-_/\\.,;:!?()&\s]+/g, '');

/**
 * Levenshtein edit distance between two strings.
 * Fast-exits with a large value when the length difference is too large.
 */
const editDistance = (a, b) => {
  if (Math.abs(a.length - b.length) > 2) return 99;
  const m = a.length;
  const n = b.length;
  const dp = Array.from({ length: m + 1 }, (_, i) => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] =
        a[i - 1] === b[j - 1]
          ? dp[i - 1][j - 1]
          : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
};

/**
 * Returns true if `token` appears anywhere in `normalisedTarget` or `collapsedTarget`
 * either as a substring or as a close word (≤1 edit for words ≥4 chars).
 *
 * Examples:
 *  token "kpop"  vs collapsedTarget "kpop" (from "K-Pop")  → true
 *  token "choclate" vs word "chocolate"                     → true (edit dist 1)
 */
const tokenMatchesTarget = (token, normalisedTarget, collapsedTarget) => {
  // Exact substring in spaced form: "pop" in "k pop"
  if (normalisedTarget.includes(token)) return true;
  // Exact substring in collapsed form: "kpop" in "kpop" (from "K-Pop")
  if (collapsedTarget.includes(token)) return true;
  // Also collapse the token itself: "k pop" (typed with space) → "kpop" vs collapsedTarget
  const collapsedToken = normalizeCollapsed(token);
  if (collapsedToken.length >= 2 && collapsedTarget.includes(collapsedToken)) return true;
  // Fuzzy match individual words
  if (token.length >= 4) {
    const words = normalisedTarget.split(' ');
    return words.some((w) => w.length >= 3 && editDistance(token, w) <= 1);
  }
  return false;
};

/**
 * Multi-keyword fuzzy search with separator normalisation.
 *
 * Splits the query into tokens (on whitespace/separators).
 * A result passes only if EVERY token matches at least one of the supplied fields.
 *
 * @param {string}   rawQuery - raw user input
 * @param {string[]} fields   - searchable text strings for one item
 * @returns {boolean}
 */
export const multiKeywordMatch = (rawQuery, fields) => {
  const tokens = normalize(rawQuery)
    .split(' ')
    .filter((t) => t.length > 0);
  if (tokens.length === 0) return true;
  const normFields = fields.map(normalize);
  const collapsedFields = fields.map(normalizeCollapsed);
  return tokens.every((token) =>
    normFields.some((field, i) => tokenMatchesTarget(token, field, collapsedFields[i]))
  );
};
